import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import {MimeType} from "@reva/shared/mime-type";
import type {ValueOrderDocument} from "@reva/shared/value-order";

import {IdpEnergyApiInterface} from "../api/interface/idp-energy";
import type {IdpEnergyResult} from "../api/payload-types/idp-energy";
import {ErrorI18n} from "../i18n";
import type {QueryError} from "../util/errorUtil";
import {buildApiQueryError, generateDefaultQueryError} from "../util/errorUtil";
import {selectCredentials} from "./authSlice";
import {downloadDocumentAsync} from "./documentsThunk";
import type {RevaThunkAction} from "./revaHooks";
import type {RevaExtraArgs} from "./store";
import type {ApiQueryError} from "./types";

interface IdpEnergyState {
    /** IDP request is running. */
    loading: boolean;
    /** Name of the file the current result was extracted from (for display). */
    fileName: string | null;
    /** Extracted values keyed by REVA parameter key, or null if no extraction has run / was cleared. */
    values: IdpEnergyResult | null;
    /** True when the service returned successfully but could not extract any data (HTTP 204). */
    noData: boolean;
    /** Query error, or null. */
    queryError: ApiQueryError | null;
}

const initialState: IdpEnergyState = {
    loading: false,
    fileName: null,
    values: null,
    noData: false,
    queryError: null,
};

export const idpEnergySlice = createSlice({
    name: "idpEnergy",
    initialState,
    reducers: {
        clearIdpEnergy: () => initialState,
        setIdpEnergyLoading: (state, action: PayloadAction<string>) => {
            state.loading = true;
            state.fileName = action.payload;
            state.values = null;
            state.noData = false;
            state.queryError = null;
        },
        setIdpEnergyResult: (state, action: PayloadAction<IdpEnergyResult>) => {
            state.loading = false;
            state.values = action.payload;
            state.noData = Object.keys(action.payload).length === 0;
            state.queryError = null;
        },
        setIdpEnergyError: (state, action: PayloadAction<QueryError>) => {
            state.loading = false;
            state.values = null;
            state.noData = false;
            state.queryError = buildApiQueryError(action.payload);
        },
    },
    selectors: {
        selectIdpEnergyLoading: (state) => state.loading,
        selectIdpEnergyFileName: (state) => state.fileName,
        selectIdpEnergyValues: (state) => state.values,
        selectIdpEnergyNoData: (state) => state.noData,
        selectIdpEnergyError: (state) => state.queryError,
    },
});

export const {clearIdpEnergy, setIdpEnergyLoading, setIdpEnergyResult, setIdpEnergyError} = idpEnergySlice.actions;

export const {selectIdpEnergyLoading, selectIdpEnergyValues, selectIdpEnergyError} = idpEnergySlice.selectors;

export default idpEnergySlice.reducer;

/**
 * Run the energy-certificate IDP extraction for an uploaded file. The result is stored for display
 * only — values are applied to the object parameters solely on explicit user take-over.
 * @param file the uploaded energy-certificate file (PDF)
 */
export const extractIdpEnergyAsync =
    (file: File): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {env, serviceApi}: RevaExtraArgs) => {
        const credentials = selectCredentials(getState());
        if (!credentials) {
            return;
        }
        dispatch(setIdpEnergyLoading(file.name));
        try {
            const body = await file.arrayBuffer();
            const apiInterface = new IdpEnergyApiInterface(file.name, body);
            const response = await apiInterface.execute(serviceApi, credentials);
            dispatch(setIdpEnergyResult(response ?? {}));
        } catch (err) {
            if (env.isDevelopmentMode) {
                console.log("IDP energy extraction failed", err);
            }
            if (err instanceof ErrorI18n) {
                dispatch(
                    setIdpEnergyError({
                        error: err.message,
                        errorArgs: {errorData: {...err.messageArgs}},
                        errorHasContactLink: false,
                    }),
                );
            } else {
                dispatch(setIdpEnergyError(generateDefaultQueryError(err as Error)));
            }
        }
    };

/**
 * Re-run the energy-certificate IDP extraction on an ALREADY uploaded document: download the stored file and
 * feed it through the same extraction flow as the upload auto-trigger. Used by the permission-gated (reva-idp-energy)
 * "re-run" button. Only meaningful for PDF documents (the IDP-Energy /energy-certificate endpoint is PDF-only).
 * @param document the existing energy-certificate document to re-extract
 */
export const rerunIdpEnergyAsync =
    (document: ValueOrderDocument): RevaThunkAction<Promise<void>> =>
    async (dispatch) => {
        // Modal direkt mit Spinner zeigen, bereits während das bestehende Dokument geladen wird.
        dispatch(setIdpEnergyLoading(document.filename));
        let file: File;
        try {
            const blob: Blob = await dispatch(downloadDocumentAsync(document));
            file = new File([blob], document.filename, {type: blob.type || MimeType.PDF});
        } catch (err) {
            dispatch(setIdpEnergyError(generateDefaultQueryError(err as Error)));
            return;
        }
        await dispatch(extractIdpEnergyAsync(file));
    };
